import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, getDoc, setDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { setSentryUser, clearSentryUser } from '../lib/sentry';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);

  // Login function
  const login = async (email, password) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      return result;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setProfile(null);
      clearSentryUser();
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  // Identity-only bootstrap: ensure /users/{uid} exists.
  // No companies, memberships, or roles are created here.
  const ensureUserDocument = async (firebaseUser) => {
    if (!firebaseUser?.uid || !db) return;

    const uid = firebaseUser.uid;
    const userDocRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userDocRef);

    if (!userDoc.exists()) {
      await setDoc(
        userDocRef,
        {
          email: firebaseUser.email,
          companyId: null,
          onboardingComplete: false,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    }
  };

  // Auth state listener
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);

      if (firebaseUser) {
        // Attach uid to Sentry (no email/name — PII-safe by default).
        setSentryUser({ uid: firebaseUser.uid });

        // Ensure user document exists
        await ensureUserDocument(firebaseUser);

        // NOTE: Company provisioning moved behind authenticated routes (ProtectedRoute)
        // to avoid running Firestore reads before auth + companyId are ready.

        // Set up Firestore profile listener
        const userDocRef = doc(db, 'users', firebaseUser.uid);
        const unsubscribeProfile = onSnapshot(
          userDocRef,
          (docSnap) => {
            if (docSnap.exists()) {
              const profileData = { id: docSnap.id, ...docSnap.data() };
              setProfile(profileData);
              // Stamp companyId on Sentry events for tenant-aware filtering.
              if (profileData.companyId) {
                setSentryUser({
                  uid: firebaseUser.uid,
                  companyId: profileData.companyId,
                });
              }
            } else {
              setProfile(null);
            }
            setIsAuthLoading(false);
          },
          (error) => {
            console.error('Profile snapshot error:', error);
            setProfile(null);
            setIsAuthLoading(false);
          }
        );

        // Return cleanup function for profile listener
        return () => {
          unsubscribeProfile();
        };
      } else {
        setProfile(null);
        clearSentryUser();
        setIsAuthLoading(false);
      }
    });

    // Cleanup auth listener
    return () => {
      unsubscribeAuth();
    };
  }, []);

  const value = {
    user,
    profile,
    isAuthLoading,
    login,
    logout,
    setUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;